// =============================================================================
// FILE: packages/renderer/src/lib/voice/voiceService.ts
// PURPOSE: Provider-independent R2 text-to-speech layer. Works with NO external
//   credentials via the browser's Web Speech (SpeechSynthesis) API, and
//   transparently upgrades to ElevenLabs (Bella / Roger) when the backend
//   reports a configured key. Adding future providers only touches this file.
// =============================================================================

export type R2VoiceId = 'r2-female' | 'r2-male' | 'r2-prime';
export type R2Provider = 'auto' | 'browser' | 'piper' | 'elevenlabs';

export interface VoiceInfo {
  id: R2VoiceId;
  label: string;
  gender: 'female' | 'male';
  sample_url: string;
  piper_ready?: boolean;
}

export interface VoiceConfig {
  active_provider: 'browser' | 'piper' | 'elevenlabs';
  elevenlabs_configured: boolean;
  piper_available: boolean;
  providers: { id: string; label: string; requires_key: boolean; available: boolean }[];
  voices: VoiceInfo[];
  default_voice: R2VoiceId;
  intro: string;
}

let currentAudio: HTMLAudioElement | null = null;
let lastUtterance: SpeechSynthesisUtterance | null = null;

export async function fetchVoiceConfig(): Promise<VoiceConfig> {
  const r = await fetch('/api/voice/config');
  if (!r.ok) throw new Error(`voice config HTTP ${r.status}`);
  return (await r.json()) as VoiceConfig;
}

// Web Speech voices load asynchronously; call once on mount to warm the list.
export function warmBrowserVoices(): void {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.getVoices();
    synth.onvoiceschanged = () => synth.getVoices();
  } catch { /* unsupported */ }
}

export function browserVoiceSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

function pickBrowserVoice(gender: 'female' | 'male'): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis?.getVoices() ?? [];
  if (!voices.length) return null;
  const femaleHints = ['female', 'samantha', 'victoria', 'karen', 'moira', 'tessa', 'zira', 'susan', 'fiona', 'bella', 'aria', 'jenny', 'google uk english female'];
  const maleHints = ['male', 'daniel', 'alex', 'fred', 'oliver', 'thomas', 'david', 'george', 'roger', 'guy', 'google uk english male'];
  const hints = gender === 'female' ? femaleHints : maleHints;
  const en = voices.filter((v) => v.lang.toLowerCase().startsWith('en'));
  const pool = en.length ? en : voices;
  const match = pool.find((v) => hints.some((h) => v.name.toLowerCase().includes(h)));
  return match ?? pool[0] ?? null;
}

export function stopSpeaking(): void {
  try { window.speechSynthesis?.cancel(); } catch { /* */ }
  lastUtterance = null;
  if (currentAudio) {
    try { currentAudio.pause(); currentAudio.currentTime = 0; } catch { /* */ }
    currentAudio = null;
  }
}

export function pauseSpeaking(): void {
  try { window.speechSynthesis?.pause(); } catch { /* */ }
  if (currentAudio) {
    try { currentAudio.pause(); } catch { /* */ }
  }
}

export function resumeSpeaking(): void {
  try { window.speechSynthesis?.resume(); } catch { /* */ }
  if (currentAudio) {
    try { void currentAudio.play(); } catch { /* */ }
  }
}

// Per-voice browser personality (rate/pitch) so each R2 identity sounds distinct.
const BROWSER_PERSONALITY: Record<R2VoiceId, { rate: number; pitch: number; gender: 'female' | 'male' }> = {
  'r2-female': { rate: 1.0,  pitch: 1.06, gender: 'female' },
  'r2-male':   { rate: 1.0,  pitch: 0.92, gender: 'male' },
  'r2-prime':  { rate: 0.88, pitch: 0.6,  gender: 'male' }, // deep, commanding leader
};

function speakBrowser(text: string, voiceId: R2VoiceId, speed = 1): Promise<void> {
  return new Promise((resolve) => {
    if (!browserVoiceSupported()) { resolve(); return; }
    const p = BROWSER_PERSONALITY[voiceId] ?? BROWSER_PERSONALITY['r2-female'];
    const u = new SpeechSynthesisUtterance(text);
    const v = pickBrowserVoice(p.gender);
    if (v) u.voice = v;
    u.rate = Math.max(0.65, Math.min(1.45, p.rate * speed));
    u.pitch = p.pitch;
    lastUtterance = u;
    u.onend = () => resolve();
    u.onerror = () => resolve();
    try { window.speechSynthesis.cancel(); window.speechSynthesis.speak(u); }
    catch { resolve(); }
  });
}

function playDataUrl(url: string, speed = 1): Promise<void> {
  return new Promise((resolve) => {
    try {
      currentAudio = new Audio(url);
      currentAudio.playbackRate = Math.max(0.65, Math.min(1.45, speed));
      currentAudio.onended = () => resolve();
      currentAudio.onerror = () => resolve();
      void currentAudio.play().catch(() => resolve());
    } catch { resolve(); }
  });
}

/**
 * Speak `text` in the selected R2 voice through the chosen engine (provider).
 * Returns the provider actually used. Falls back to the browser voice on any
 * failure, so the caller never has to branch on provider availability.
 *   provider 'browser' → always device engine (Tier 1)
 *   provider 'piper'   → self-hosted neural (Tier 2), else browser
 *   provider 'elevenlabs'/'auto' → premium/best available, else browser
 */
export async function speak(text: string, voice: R2VoiceId, provider: R2Provider = 'auto', speed = 1): Promise<'elevenlabs' | 'piper' | 'browser'> {
  stopSpeaking();
  const clean = text.trim();
  if (!clean) return 'browser';
  if (provider !== 'browser') {
    try {
      const r = await fetch('/api/voice/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: clean, voice, provider, speed }),
      });
      if (r.ok) {
        const d = (await r.json()) as { use_browser?: boolean; audio_url?: string; provider?: 'elevenlabs' | 'piper' | 'browser' };
        if (!d.use_browser && d.audio_url) {
          await playDataUrl(d.audio_url, speed);
          return d.provider === 'piper' ? 'piper' : 'elevenlabs';
        }
      }
    } catch { /* network / provider error — fall back */ }
  }
  await speakBrowser(clean, voice, speed);
  return 'browser';
}

// Preview an uploaded sample clip for a voice.
export function playSample(url: string): void {
  stopSpeaking();
  void playDataUrl(url);
}
